import { REQUIRED_ENV } from './requiredEnv';

export interface AuthEnv {
  /** OIDC issuer of the Authentik provider `bizhost-admin`. */
  authority: string;
  /** Public client id of that provider. */
  clientId: string;
  scope: string;
}

export interface AppEnv {
  /** Backend2.0 origin, without the `/api` suffix (the HttpClient adds it). */
  apiBaseUrl: string;
  /** Environment label: dev | test | prod. */
  environment: string;
  auth: AuthEnv;
}

// offline_access makes Authentik issue a refresh token, which is what lets
// the session renew silently instead of bouncing to the login page every
// five minutes (the access token lifetime).
const DEFAULT_AUTH_SCOPE = 'openid profile email offline_access';

export type EnvSource = Record<string, string | boolean | undefined>;

/** Raised when the build lacks one or more required variables. */
export class MissingEnvError extends Error {
  readonly missing: string[];

  constructor(missing: string[]) {
    super(`Missing required environment variable(s): ${missing.join(', ')}`);
    this.name = 'MissingEnvError';
    this.missing = missing;
  }
}

function read(source: EnvSource, name: string): string {
  const value = source[name];
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Validate and map build-time configuration. Side-effect free, so the
 * bootstrap can check it before importing anything that depends on it.
 */
export function parseEnv(source: EnvSource): AppEnv {
  const missing = REQUIRED_ENV.filter((name) => read(source, name) === '');
  if (missing.length > 0) {
    throw new MissingEnvError([...missing]);
  }

  const environment = read(source, 'VITE_ENV');
  const scope = read(source, 'VITE_AUTH_SCOPE');

  return {
    apiBaseUrl: read(source, 'VITE_API_BASE_URL').replace(/\/+$/, ''),
    environment: environment === '' ? 'dev' : environment,
    auth: {
      authority: read(source, 'VITE_AUTH_AUTHORITY'),
      clientId: read(source, 'VITE_AUTH_CLIENT_ID'),
      scope: scope === '' ? DEFAULT_AUTH_SCOPE : scope
    }
  };
}
